<template>
  <v-dialog v-model="abierto" max-width="1040" persistent scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-btn small text color="primary" class="mr-2" v-bind="attrs" v-on="on">
        <v-icon left small>mdi-creation</v-icon>
        Cargar desde PDF / foto
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon color="#BA6B30" class="mr-2">mdi-creation</v-icon>
        Cargar presupuesto desde un archivo
        <v-spacer></v-spacer>
        <v-btn icon small @click="cerrar"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Paso 1: archivo -->
        <div v-if="!lectura">
          <p class="text-body-2 grey--text text--darken-1">
            Subí el pedido del cliente, una lista con cantidades o una captura de planilla. Busco cada producto en tu
            catálogo y te muestro todo para revisar. No se guarda nada hasta que confirmes el presupuesto, y los precios
            salen de tu lista, no del archivo.
          </p>
          <v-file-input
            v-model="archivo"
            accept=".pdf,image/png,image/jpeg,image/webp"
            label="PDF o foto del pedido"
            prepend-icon="mdi-file-document-outline"
            outlined dense show-size
            :disabled="leyendo"
          ></v-file-input>
          <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
          <div class="d-flex">
            <v-spacer></v-spacer>
            <v-btn depressed color="primary" :disabled="!archivo" :loading="leyendo" @click="leer">Leer archivo</v-btn>
          </div>
          <p v-if="leyendo" class="text-caption grey--text mt-2">Leyendo el archivo… suele tardar entre 10 y 20 segundos.</p>
        </div>

        <!-- Paso 2: revisión -->
        <div v-else>
          <v-alert v-if="!lectura.verificacion.cierra" type="warning" dense text>
            <b>Los números del archivo no cierran.</b>
            <div v-for="(p, i) in lectura.verificacion.problemas" :key="i" class="text-caption">• {{ p }}</div>
          </v-alert>
          <v-alert v-if="lectura.observaciones" type="info" dense text class="text-caption">{{ lectura.observaciones }}</v-alert>

          <div class="text-caption grey--text">Cliente</div>
          <div class="text-body-2" v-if="clienteLeido">
            En el archivo: <b>{{ clienteLeido }}</b>
          </div>
          <div class="text-caption orange--text text--darken-2" v-else>
            El archivo no dice quién es el cliente: lo elegís en el formulario.
          </div>
          <v-select
            v-if="opcionesCliente.length"
            v-model="clienteId"
            :items="opcionesCliente"
            item-text="nombre_completo" item-value="id"
            dense outlined hide-details clearable
            label="Cliente en el sistema"
            class="mt-2 cliente"
          ></v-select>
          <div class="text-caption grey--text mt-1" v-if="lectura.cliente.propuesto">
            Encontrado por {{ lectura.cliente.por }}.
          </div>

          <v-simple-table dense class="mt-4 lineas">
            <thead>
              <tr>
                <th>En el archivo</th>
                <th style="width: 42%">Producto en el sistema</th>
                <th class="text-right">Cant.</th>
                <th class="text-right">Precio archivo</th>
                <th class="text-right">Tu lista</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(l, i) in lineas" :key="i" :class="estadoLinea(l)">
                <td>
                  <div class="text-body-2">{{ l.descripcion }}</div>
                  <div class="text-caption grey--text">
                    <span v-if="l.codigo">cód. {{ l.codigo }}</span>
                    <span v-if="l.codigo && l.seccion"> · </span>
                    <span v-if="l.seccion">{{ l.seccion }}</span>
                  </div>
                </td>
                <td>
                  <v-select
                    v-model="l.eleccion"
                    :items="opcionesLinea(l)"
                    item-text="texto" item-value="psp_id"
                    dense outlined hide-details clearable
                    :placeholder="l.candidatos.length ? 'Elegí el producto…' : 'No está en tu catálogo'"
                  ></v-select>
                  <div class="text-caption mt-1" :class="claseOrigen(l)">{{ textoOrigen(l) }}</div>
                  <div v-for="(a, j) in avisosDe(l)" :key="j" class="text-caption aviso">
                    <v-icon x-small color="orange darken-2" class="mr-1">mdi-alert-outline</v-icon>{{ a }}
                  </div>
                </td>
                <td class="text-right">{{ l.cantidad }}</td>
                <td class="text-right">{{ moneda(l.precio_unitario) }}</td>
                <td class="text-right" :class="{ 'orange--text text--darken-3': difierePrecio(l) }">
                  {{ elegido(l) ? moneda(elegido(l).precio_lista) : "—" }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row dense class="mt-3">
            <v-col cols="12" md="7" class="text-caption grey--text">
              {{ resumen.asignadas }} de {{ lineas.length }} líneas con producto asignado
              <span v-if="resumen.sinAsignar"> · las otras {{ resumen.sinAsignar }} las agregás a mano</span>
              <div v-if="resumen.conPrecioDistinto" class="orange--text text--darken-3 mt-1">
                {{ resumen.conPrecioDistinto }} línea(s) con un precio distinto al de tu lista: el presupuesto usa el de tu lista.
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-right">
              <div class="text-caption grey--text">Total del archivo · con tu lista</div>
              <div class="text-h6 font-weight-bold">
                {{ moneda(lectura.total) }}
                <span class="text-body-2 grey--text"> · {{ moneda(totalLista) }}</span>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions v-if="lectura">
        <v-btn text @click="reiniciar">Otro archivo</v-btn>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text mr-3">{{ (lectura.meta.duracion_ms / 1000).toFixed(1) }} s</span>
        <v-btn depressed color="primary" :disabled="!resumen.asignadas" @click="aplicar">
          Pasar al formulario
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api/asistente";

const ORIGEN = {
  exacto: { texto: "Coinciden el producto y el precio", clase: "green--text text--darken-2" },
  numero: { texto: "Por el código y el nombre: confirmalo", clase: "orange--text text--darken-3" },
  ia: { texto: "Propuesto por el asistente: confirmalo", clase: "orange--text text--darken-3" },
};

export default {
  name: "PresupuestoDesdeArchivo",
  data() {
    return {
      abierto: false,
      archivo: null,
      leyendo: false,
      error: "",
      lectura: null,
      clienteId: null,
      lineas: [],
    };
  },
  computed: {
    clienteLeido() {
      const c = this.lectura.cliente.leido;
      return [c.nombre, c.cuit].filter(Boolean).join(" · ");
    },
    opcionesCliente() {
      const c = this.lectura.cliente;
      const items = c.propuesto ? [c.propuesto] : [];
      c.candidatos.forEach((x) => {
        if (!items.find((i) => i.id === x.id)) items.push(x);
      });
      return items;
    },
    resumen() {
      const asignadas = this.lineas.filter((l) => l.eleccion).length;
      return {
        asignadas,
        sinAsignar: this.lineas.length - asignadas,
        conPrecioDistinto: this.lineas.filter((l) => this.difierePrecio(l)).length,
      };
    },
    totalLista() {
      return this.lineas.reduce((t, l) => {
        const e = this.elegido(l);
        return e ? t + Number(e.precio_lista) * Number(l.cantidad || 0) : t;
      }, 0);
    },
  },
  methods: {
    moneda(v) {
      if (v === null || v === undefined || v === "") return "—";
      return "$ " + Number(v).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async leer() {
      this.leyendo = true;
      this.error = "";
      try {
        const { data } = await api.leerPresupuesto(this.archivo);
        this.lectura = data;
        this.clienteId = data.cliente.propuesto ? data.cliente.propuesto.id : null;
        this.lineas = data.lineas.map((l) => ({ ...l, eleccion: l.propuesta ? l.propuesta.psp_id : null }));
      } catch (e) {
        const detalle = e.response && e.response.data && e.response.data.error;
        this.error = detalle || "No pude leer el archivo. Probá con otro o cargá el presupuesto a mano.";
      } finally {
        this.leyendo = false;
      }
    },
    opcionesLinea(l) {
      return [l.propuesta, ...l.candidatos].filter(Boolean).map((c) => ({
        psp_id: c.psp_id,
        texto: `${c.nombre} · ${this.moneda(c.precio_lista)}`,
      }));
    },
    elegido(l) {
      return [l.propuesta, ...l.candidatos].filter(Boolean).find((c) => c.psp_id === l.eleccion) || null;
    },
    difierePrecio(l) {
      const e = this.elegido(l);
      if (!e || l.precio_unitario === null || l.precio_unitario === undefined) return false;
      return Math.abs(Number(e.precio_lista) - Number(l.precio_unitario)) > 0.01;
    },
    esLaPropuesta(l) {
      return l.propuesta && l.eleccion === l.propuesta.psp_id;
    },
    // Los avisos del backend son sobre la propuesta; si el usuario eligió otro producto ya no aplican
    avisosDe(l) {
      if (this.esLaPropuesta(l)) return l.avisos;
      if (this.difierePrecio(l)) return [`El archivo dice ${this.moneda(l.precio_unitario)}: se guarda el precio de tu lista.`];
      return [];
    },
    estadoLinea(l) {
      if (!l.eleccion) return "sin-asignar";
      return this.esLaPropuesta(l) && l.confianza === "alta" ? "seguro" : "revisar";
    },
    textoOrigen(l) {
      if (!l.eleccion) return l.candidatos.length ? "Ninguna coincidencia clara" : "No está en tu catálogo";
      if (this.esLaPropuesta(l)) return ORIGEN[l.origen].texto;
      return "Elegido por vos";
    },
    claseOrigen(l) {
      if (!l.eleccion) return "red--text text--darken-1";
      if (this.esLaPropuesta(l)) return ORIGEN[l.origen].clase;
      return "grey--text";
    },
    aplicar() {
      const cliente = this.opcionesCliente.find((c) => c.id === this.clienteId) || null;
      const detalles = [];
      const pendientes = [];
      this.lineas.forEach((l) => {
        const e = this.elegido(l);
        if (e) detalles.push({ psp: e.psp_id, descripcion: e.nombre, cantidad: Number(l.cantidad) || 1 });
        else pendientes.push(l);
      });
      this.$emit("aplicar", { cliente, detalles, pendientes });
      this.cerrar();
    },
    reiniciar() {
      this.lectura = null;
      this.lineas = [];
      this.clienteId = null;
      this.archivo = null;
      this.error = "";
    },
    cerrar() {
      this.abierto = false;
      this.reiniciar();
    },
  },
};
</script>

<style scoped>
.cliente { max-width: 420px; }
.lineas tr.seguro td:nth-child(2) { border-left: 3px solid #43a047; }
.lineas tr.revisar td:nth-child(2) { border-left: 3px solid #fb8c00; }
.lineas tr.sin-asignar td:nth-child(2) { border-left: 3px solid #e53935; }
.lineas td { padding-top: 6px !important; padding-bottom: 6px !important; vertical-align: top; }
.aviso { color: #b45309; line-height: 1.35; margin-top: 2px; }
</style>
